import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/admin/ImagePicker";

const FIELDS = [
  { group: "Contact", keys: [
    ["contact_email", "Email de contact"], ["phone", "Téléphone"], ["address", "Adresse"],
  ]},
  { group: "Réseaux sociaux", keys: [
    ["social_facebook", "Facebook (URL)"], ["social_instagram", "Instagram (URL)"], ["social_youtube", "YouTube (URL)"],
  ]},
  { group: "Application compagnon", keys: [
    ["app_url", "URL de l'application"],
  ]},
  { group: "SEO", keys: [
    ["site_url", "URL canonique du site"], ["seo_title", "Titre SEO par défaut"], ["seo_description", "Meta description par défaut"],
  ]},
  { group: "Société", keys: [
    ["site_name", "Nom du site"], ["legal_form", "Forme juridique"], ["siret", "SIRET"],
  ]},
  { group: "Bandeau d'annonce", keys: [
    ["announcement_text", "Texte du bandeau"],
  ]},
];

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  if (!s) return <p className="text-muted-foreground">Chargement…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings" className="max-w-3xl">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx mb-8">Paramètres</h1>

      {FIELDS.map(({ group, keys }) => (
        <section key={group} className="bg-white border border-line p-6 sm:p-8 mb-6">
          <h2 className="font-display font-bold text-lg text-onyx mb-5">{group}</h2>
          <div className="space-y-4">
            {keys.map(([key, label]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Input data-testid={`settings-${key}`} className="mt-1.5" value={s[key] || ""}
                  onChange={(e) => setS({ ...s, [key]: e.target.value })} />
              </div>
            ))}
          </div>
          {group === "Bandeau d'annonce" && (
            <div className="flex items-center gap-3 mt-4">
              <Switch data-testid="settings-announcement-active" checked={s.announcement_active === "true"}
                onCheckedChange={(v) => setS({ ...s, announcement_active: v ? "true" : "false" })} />
              <Label>Bandeau visible</Label>
            </div>
          )}
          {group === "SEO" && (
            <div className="mt-4">
              <ImagePicker label="Image Open Graph par défaut" value={s.og_image} onChange={(url) => setS({ ...s, og_image: url })} />
            </div>
          )}
        </section>
      ))}

      <section className="bg-white border border-line p-6 sm:p-8 mb-6">
        <h2 className="font-display font-bold text-lg text-onyx mb-5">Identité visuelle</h2>
        <div className="space-y-5">
          <ImagePicker label="Logo du site (fond clair — header)" value={s.logo_url} onChange={(url) => setS({ ...s, logo_url: url })} />
          <ImagePicker label="Logo du site (fond sombre — footer)" value={s.logo_url_light} onChange={(url) => setS({ ...s, logo_url_light: url })} />
          <ImagePicker label="Photo principale du produit" value={s.product_photo_url} onChange={(url) => setS({ ...s, product_photo_url: url })} />
        </div>
      </section>

      <section className="bg-sand border border-line p-6 mb-6 text-sm text-muted-foreground">
        <p className="font-semibold text-onyx mb-1">Informations légales</p>
        <p>DOUZIEME HOMME LE JEU — {s.legal_form} — SIRET {s.siret}</p>
        <p className="mt-2 text-xs">L'email et le mot de passe administrateur se modifient via les variables d'environnement du serveur (ADMIN_EMAIL / ADMIN_PASSWORD).</p>
      </section>

      <button onClick={save} disabled={saving} data-testid="settings-save"
        className="inline-flex items-center gap-2 bg-rouge text-white px-8 py-3.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
      </button>
    </div>
  );
}
